import {Component, Input, OnInit} from '@angular/core';
import {Country} from '../../../../../../../common/api';
import {ValueByKeyPipe} from '../../../../../core/pipes/value.by.key.pipe';
import {ExpectedTurnover, MainServiceOwner} from '../../api/entity';
import {CorporateAccountsService} from '../../api/service';
import {cloneDeep, get, isEmpty} from 'lodash';
import {BackofficeRangesService} from '../../../../../shared/api/service/backoffice.ranges.service';
import {Range} from '../../../../../../../common/api/range';


@Component({
    selector: 'tfl-profile-detail-expected-turnover',
    template: require('./expected.turnover.template.html')
})
export class ExpectedTurnoverComponent implements OnInit {

    @Input("profileId") profileId: number;
    @Input("loading") loading: boolean = true;
    @Input("countries") countries: Country[] = [];
    @Input('systemOrWL') systemOrWL: string;
    expectedTurnover: ExpectedTurnover = null;
    originalExpectedTurnover: ExpectedTurnover = null;
    averageIncomingTransactionRanges: Array<Range>;
    averageOutgoingTransactionRanges: Array<Range>;
    plannedMonthlyIncomingTurnoverRanges: Array<Range>;
    plannedMonthlyIncomingTurnoverMoreRanges: Array<Range>;
    plannedMonthlyOutgoingTurnoverMoreRanges: Array<Range>;
    plannedMonthlyOutgoingTurnoverRanges: Array<Range>;
    monthlyIncomingFrequencyRanges: Array<Range>;
    monthlyOutgoingFrequencyRanges: Array<Range>;

    rangeMore: Array<number>;

    pattern = '^[a-zA-Z\\s-,\\.\']*$';
    isEdited: boolean = false;
    private readonly NOT_PROVIDED = 'Not provided';
    private readonly UNIT_EUR = 'EUR';
    private readonly AVERAGE_INCOMING_TRANSACTION_RANGES_GROUP_ID = 7;
    private readonly PLANNED_MONTHLY_INCOMING_TURNOVER_RANGES_GROUP_ID = 8;
    private readonly PLANNED_MONTHLY_INCOMING_TURNOVER_RANGES_MORE_GROUP_ID = 15;
    private readonly PLANNED_MONTHLY_OUTGOING_TURNOVER_RANGES_MORE_GROUP_ID = 15;
    private readonly MONTHLY_INCOMING_FREQUENCY_RANGES_GROUP_ID = 9;
    private readonly AVERAGE_OUTGOING_TRANSACTION_RANGES_GROUP_ID = 10;
    private readonly PLANNED_MONTHLY_OUTGOING_TURNOVER_RANGES_GROUP_ID = 11;
    private readonly MONTHLY_OUTGOING_FREQUENCY_RANGES_GROUP_ID = 12;

    constructor(private _corporateAccountsService: CorporateAccountsService,
                private _rangesService: BackofficeRangesService) {
    }

    @Input("expectedTurnover")
    set setExpectedTurnovers(expectedTurnover: ExpectedTurnover) {
        this.expectedTurnover = expectedTurnover;
        this.originalExpectedTurnover = cloneDeep(expectedTurnover);
    }

    ngOnInit(): void {
        this.rangeMore = [60, 61, 62];

        this._rangesService.getAllRangesByGroupId(this.AVERAGE_INCOMING_TRANSACTION_RANGES_GROUP_ID)
        .subscribe(response => this.averageIncomingTransactionRanges = response);

        this._rangesService.getAllRangesByGroupId(this.PLANNED_MONTHLY_INCOMING_TURNOVER_RANGES_GROUP_ID)
        .subscribe(response => this.plannedMonthlyIncomingTurnoverRanges = response);

        this._rangesService.getAllRangesByGroupId(this.PLANNED_MONTHLY_OUTGOING_TURNOVER_RANGES_GROUP_ID)
        .subscribe(response => this.plannedMonthlyOutgoingTurnoverRanges = response);

        this._rangesService.getAllRangesByGroupId(this.PLANNED_MONTHLY_INCOMING_TURNOVER_RANGES_MORE_GROUP_ID)
        .subscribe(response => this.plannedMonthlyIncomingTurnoverMoreRanges = response);

        this._rangesService.getAllRangesByGroupId(this.PLANNED_MONTHLY_OUTGOING_TURNOVER_RANGES_MORE_GROUP_ID)
        .subscribe(response => this.plannedMonthlyOutgoingTurnoverMoreRanges = response);

        this._rangesService.getAllRangesByGroupId(this.MONTHLY_INCOMING_FREQUENCY_RANGES_GROUP_ID)
        .subscribe(response => this.monthlyIncomingFrequencyRanges = response);

        this._rangesService.getAllRangesByGroupId(this.AVERAGE_OUTGOING_TRANSACTION_RANGES_GROUP_ID)
        .subscribe(response => this.averageOutgoingTransactionRanges = response);

        this._rangesService.getAllRangesByGroupId(this.MONTHLY_OUTGOING_FREQUENCY_RANGES_GROUP_ID)
        .subscribe(response => this.monthlyOutgoingFrequencyRanges = response);
    }

    onChangeInputValue(event, fieldName) {
        this.expectedTurnover[fieldName] = event.target.value;
    }

    onChangeInputAmount(event, fieldName) {
        if (isEmpty(get(event, "target.value", ""))) {
            this.expectedTurnover[fieldName] = undefined;
        } else {
            this.expectedTurnover[fieldName] = {};
            this.expectedTurnover[fieldName].amount = event.target.value;
            this.expectedTurnover[fieldName].currency_code = "EUR";
        }
    }

    onChangeInputMainSuppliers(event, index) {
        this.expectedTurnover.mainSuppliers[index].companyName = event.target.value;
    }

    onChangeInputMainClients(event, index) {
        this.expectedTurnover.mainClients[index].companyName = event.target.value;
    }

    getMainSuppliers(): string {
        return this.expectedTurnover
        .mainSuppliers
        .map(item => `${item.companyName}/${new ValueByKeyPipe().transform(item.country, this.countries)}`)
        .join(", ");
    }

    getMainClients(): string {
        return this.expectedTurnover
        .mainClients
        .map(item => `${item.companyName}/${new ValueByKeyPipe().transform(item.country, this.countries)}`)
        .join(", ");
    }

    onEdit() {
        this.isEdited = true;
    }

    onDiscard(): void {
        this.expectedTurnover = cloneDeep(this.originalExpectedTurnover);
        this.isEdited = false;
    }

    onSave(): void {
        this.createStubsForApiFields();

        this._corporateAccountsService.updateExpectedTurnover(this.profileId, this.expectedTurnover)
        .subscribe((expectedTurnover) => this.expectedTurnover = cloneDeep(expectedTurnover));
        this.originalExpectedTurnover = cloneDeep(this.expectedTurnover);
        this.isEdited = false;
    }

    addMainSuppliers(): void {
        this.expectedTurnover.mainSuppliers.push(this.createNewMainServiceOwner())
    }

    addMainClients(): void {
        this.expectedTurnover.mainClients.push(this.createNewMainServiceOwner())
    }

    removeMainSupplier(index: number): void {
        this.expectedTurnover.mainSuppliers.splice(index, 1);
    }

    removeMainClient(index: number): void {
        this.expectedTurnover.mainClients.splice(index, 1);
    }

    createNewMainServiceOwner(): MainServiceOwner {
        return {
            companyName: "",
            country: null
        }
    }

    isMoreIncomingRange() {
        return this.rangeMore.indexOf(this.expectedTurnover.plannedIncomingTurnoverAmount) !== -1;
    }

    isMoreOutgoingRange() {
        return this.rangeMore.indexOf(this.expectedTurnover.plannedOutgoingTurnoverAmount) !== -1;
    }

    private createStubsForApiFields() {
        this.expectedTurnover.mainSuppliers.forEach(supplier => supplier.companyName = this.NOT_PROVIDED);
        this.expectedTurnover.mainClients.forEach(client => client.companyName = this.NOT_PROVIDED);
    }


}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/customers/shared/components/expected.turnover/expected.turnover.component.ts